import React from 'react';
import { TourQuestionnaire, TouristSite, Location } from '../../../types';
import { cn } from '../../../lib/utils';
import { CheckCircle2, Star, Accessibility, PawPrint, Ban, Info, UtensilsCrossed, Route } from 'lucide-react';
import { getDistance } from '../../../services/circuitEngine';

interface Step9_6Props {
  questionnaire: TourQuestionnaire;
  setQuestionnaire: React.Dispatch<React.SetStateAction<TourQuestionnaire>>;
  sites: TouristSite[];
  currentDay: number;
  moment: 'lunch' | 'dinner';
  setSelectedActivity: (site: TouristSite, moment?: 'lunch' | 'dinner') => void;
  setPreviousStep: (step: number) => void;
  setStep: (step: number) => void;
  handleNext: () => void;
  handleBack: () => void;
  getRestaurantScore: (site: TouristSite) => { color: string; score: number; reason: string };
  proposedCircuit: any;
}

// A site is a catering site if it has cateringType set, or the 'restauration' category,
// or isCatering flag — any of these qualifies.
const isCateringSite = (site: TouristSite): boolean =>
  site.isCatering === true ||
  (site.categories || []).some(c => c.toLowerCase() === 'restauration') ||
  site.cateringType === 'restaurant' ||
  site.cateringType === 'snack';

export const Step9_6: React.FC<Step9_6Props> = ({
  questionnaire,
  setQuestionnaire,
  sites,
  currentDay,
  moment,
  setSelectedActivity,
  setPreviousStep,
  setStep,
  handleNext,
  handleBack,
  getRestaurantScore,
  proposedCircuit,
}) => {
  // Find reference location from the circuit (nearest activity before the meal)
  let referenceLocation: Location | null = null;

  if (proposedCircuit?.steps) {
    let dayCount = 0;
    for (let i = 0; i < proposedCircuit.steps.length; i++) {
      const s = proposedCircuit.steps[i];
      if (s.type === 'pickup') dayCount++;
      if (dayCount === currentDay) {
        if (moment === 'lunch' && s.type === 'meal' && !s.label?.includes('Dîner')) {
          for (let j = i - 1; j >= 0; j--) {
            if (proposedCircuit.steps[j].type === 'activity' && proposedCircuit.steps[j].site) {
              referenceLocation = proposedCircuit.steps[j].site.location;
              break;
            }
          }
          break;
        }
        if (moment === 'dinner' && s.type === 'meal' && s.label?.includes('Dîner')) {
          const accId = questionnaire.selectedAccommodations?.[currentDay];
          referenceLocation = accId
            ? sites.find(s2 => s2.id === accId)?.location ?? null
            : null;
          break;
        }
      }
    }
  }

  const accommodationId = questionnaire.selectedAccommodations?.[currentDay];

  // Sort: accommodation with catering first (bonus), then by score, then by distance, then by rating
  const relevantRestaurants = sites
    .filter(site => {
      if (!isCateringSite(site)) return false;
      if (site.sector !== questionnaire.sector) return false;
      const score = getRestaurantScore(site);
      if (score.color === 'red') return false;
      if (referenceLocation) {
        const dist = getDistance(referenceLocation, site.location);
        if (dist > 15) return false;
      }
      return true;
    })
    .sort((a, b) => {
      // 1. Accommodation with catering gets top priority
      const aIsAccommo = a.id === accommodationId;
      const bIsAccommo = b.id === accommodationId;
      if (aIsAccommo && !bIsAccommo) return -1;
      if (!aIsAccommo && bIsAccommo) return 1;

      // 2. Score match
      const scoreA = getRestaurantScore(a).score;
      const scoreB = getRestaurantScore(b).score;
      if (scoreA !== scoreB) return scoreB - scoreA;

      // 3. Distance
      if (referenceLocation) {
        const dA = getDistance(referenceLocation, a.location);
        const dB = getDistance(referenceLocation, b.location);
        if (Math.abs(dA - dB) > 2) return dA - dB;
      }

      // 4. Rating
      return (b.rating || b.googleRating || 0) - (a.rating || a.googleRating || 0);
    });

  const nearestRestaurant = sites
    .filter(s => isCateringSite(s) && s.sector === questionnaire.sector && getRestaurantScore(s).color !== 'red')
    .map(s => ({
      site: s,
      distance: referenceLocation ? getDistance(referenceLocation, s.location) : Infinity,
    }))
    .sort((a, b) => a.distance - b.distance)[0];

  const handleSelectPicnic = () => {
    const selectedRestaurants = { ...(questionnaire.selectedRestaurants || {}) };
    if (!selectedRestaurants[currentDay]) selectedRestaurants[currentDay] = {};
    selectedRestaurants[currentDay][moment] = 'picnic';

    const dailyCatering = { ...(questionnaire.dailyCatering || {}) };
    if (!dailyCatering[currentDay]) {
      dailyCatering[currentDay] = { lunch: { type: null }, eveningMealIncluded: false };
    }
    if (moment === 'lunch') {
      dailyCatering[currentDay] = {
        ...dailyCatering[currentDay],
        lunch: { ...dailyCatering[currentDay].lunch, type: 'pique-nique' },
      };
    }

    setQuestionnaire(prev => ({ ...prev, selectedRestaurants, dailyCatering }));
    handleNext();
  };

  const isSelected = !!questionnaire.selectedRestaurants?.[currentDay]?.[moment];

  return (
    <div className="flex flex-col h-full p-0 overflow-hidden bg-zinc-950">
      <div className="shrink-0 p-6 space-y-2">
        <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
          {moment === 'lunch' ? 'Déjeuner' : 'Dîner'} — Jour {currentDay}
        </h2>
        <p className="text-zinc-400 text-xs">
          {moment === 'lunch'
            ? "Sélectionnez votre établissement pour la pause de midi."
            : "Sélectionnez votre établissement pour le repas du soir."}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-6 space-y-3 custom-scrollbar pb-32">
        {relevantRestaurants.length > 0 ? (
          relevantRestaurants.map(site => {
            const score = getRestaurantScore(site);
            const isCurrentSelected = questionnaire.selectedRestaurants?.[currentDay]?.[moment] === site.id;
            const isAccommo = site.id === accommodationId;
            const isSnack = (site.forks ?? 0) === 0;

            return (
              <div key={site.id} className="relative">
                <button
                  onClick={() => {
                    const selectedRestaurants = { ...(questionnaire.selectedRestaurants || {}) };
                    if (!selectedRestaurants[currentDay]) selectedRestaurants[currentDay] = {};
                    selectedRestaurants[currentDay][moment] = site.id;
                    setQuestionnaire(prev => ({ ...prev, selectedRestaurants }));
                  }}
                  className={cn(
                    "w-full p-3.5 text-left transition-all flex items-center gap-4 rounded-xl border backdrop-blur-sm",
                    isCurrentSelected
                      ? "bg-emerald-500/10 border-emerald-500"
                      : isAccommo
                        ? "bg-blue-500/5 border-blue-500/40 hover:border-blue-500"
                        : "bg-zinc-900 border-zinc-800 hover:border-zinc-700"
                  )}
                >
                  <div className="w-16 h-16 rounded-lg bg-zinc-800 overflow-hidden shrink-0 border border-white/10 relative">
                    {site.image && site.image.length > 0 && !site.image.includes('maps.googleapis.com') ? (
                      <img src={site.image} alt={site.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-2xl">🍽️</div>
                    )}
                    {isCurrentSelected && (
                      <div className="absolute top-0 left-0 w-7 h-7 bg-emerald-500 rounded-br-xl flex items-center justify-center shadow-lg z-10">
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      </div>
                    )}
                    {isAccommo && (
                      <div className="absolute bottom-0 right-0 bg-blue-500 px-1.5 py-0.5 rounded-tl-lg text-[8px] font-bold text-white uppercase tracking-tighter">
                        Votre Logement
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-2 mb-1">
                      <div className="flex flex-col">
                        <h3 className="font-bold text-white text-sm leading-tight truncate">{site.name}</h3>
                        <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-400 mt-0.5">
                          {isSnack ? 'Snack' : 'Restaurant'}
                        </span>
                      </div>
                      <div className={cn(
                        "w-3 h-3 rounded-full shrink-0 mt-1",
                        score.color === 'green' ? "bg-emerald-500" :
                        score.color === 'yellow' ? "bg-yellow-500" : "bg-orange-500"
                      )} />
                    </div>
                    <p className="text-[10px] text-zinc-400 line-clamp-2 mb-2 leading-relaxed">{site.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1 text-amber-400">
                          <Star className="w-3 h-3 fill-current" />
                          <span className="text-[10px] font-bold">{site.rating || site.googleRating || "N/A"}</span>
                        </div>
                        {referenceLocation && (
                          <div className="flex items-center gap-1 text-zinc-500">
                            <Route className="w-3 h-3" />
                            <span className="text-[10px] font-bold">{Math.round(getDistance(referenceLocation, site.location))} km</span>
                          </div>
                        )}
                        {(site.forks ?? 0) > 0 && (
                          <div className="flex gap-0.5">
                            {[...Array(site.forks)].map((_, i) => (
                              <UtensilsCrossed key={i} className="w-3 h-3 text-blue-400" />
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {questionnaire.pmr && site.isHandicapAccessible && (
                          <Accessibility className="w-3.5 h-3.5 text-blue-400" />
                        )}
                        {questionnaire.hasAnimal && site.dogsNotAllowed && (
                          <div className="relative">
                            <PawPrint className="w-3.5 h-3.5 text-red-400 opacity-50" />
                            <Ban className="w-4 h-4 text-red-500 absolute -top-0.5 -left-0.5" strokeWidth={1} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
                <button
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedActivity(site, moment);
                    setPreviousStep(9.6);
                    setStep(10);
                  }}
                  className="absolute top-3 right-3 p-2 bg-zinc-800/80 hover:bg-zinc-700 rounded-full text-zinc-300 transition-colors z-20"
                >
                  <Info className="w-4 h-4" />
                </button>
              </div>
            );
          })
        ) : (
          <div className="space-y-6">
            <div className="p-8 text-center bg-zinc-900/50 rounded-3xl border border-zinc-800 border-dashed">
              <UtensilsCrossed className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-400 text-sm font-medium mb-2">Aucun établissement disponible à proximité immédiate.</p>
              <p className="text-zinc-500 text-xs leading-relaxed mb-6">
                Le système prévoit une pause pique-nique de 60 minutes pour garantir le respect de votre itinéraire.
              </p>
              <button
                onClick={handleSelectPicnic}
                className={cn(
                  "w-full px-6 py-4 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-3",
                  questionnaire.selectedRestaurants?.[currentDay]?.[moment] === 'picnic'
                    ? "bg-emerald-500 text-white shadow-[0_0_20px_rgba(16,185,129,0.3)]"
                    : "bg-emerald-500/10 border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
                )}
              >
                {questionnaire.selectedRestaurants?.[currentDay]?.[moment] === 'picnic' && <CheckCircle2 className="w-5 h-5" />}
                Valider la pause Pique-nique (60 min)
              </button>
            </div>

            {nearestRestaurant && nearestRestaurant.distance < 15 && (
              <div className="space-y-3">
                <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-2">Alternative la plus proche</p>
                <div className="relative">
                  <button
                    onClick={() => {
                      const selectedRestaurants = { ...(questionnaire.selectedRestaurants || {}) };
                      if (!selectedRestaurants[currentDay]) selectedRestaurants[currentDay] = {};
                      selectedRestaurants[currentDay][moment] = nearestRestaurant.site.id;
                      setQuestionnaire(prev => ({ ...prev, selectedRestaurants }));
                    }}
                    className={cn(
                      "w-full p-4 text-left transition-all flex items-center gap-4 rounded-2xl border backdrop-blur-sm",
                      questionnaire.selectedRestaurants?.[currentDay]?.[moment] === nearestRestaurant.site.id
                        ? "bg-emerald-500/10 border-emerald-500"
                        : "bg-zinc-900/50 border-zinc-800 hover:border-zinc-700"
                    )}
                  >
                    <div className="w-16 h-16 rounded-xl bg-zinc-800 overflow-hidden shrink-0 border border-white/10">
                      {nearestRestaurant.site.image ? (
                        <img src={nearestRestaurant.site.image} alt={nearestRestaurant.site.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl">🍽️</div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-white text-sm truncate">{nearestRestaurant.site.name}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-orange-400 font-bold">Détour de {Math.round(nearestRestaurant.distance)} km</span>
                        <span className="text-[10px] text-zinc-500">• Env. {Math.round(nearestRestaurant.distance * 1.5)} min</span>
                      </div>
                      <p className="text-[10px] text-zinc-400 mt-2 leading-relaxed">
                        Attention : ce choix peut impacter la suite de votre planning et nécessiter de retirer une activité.
                      </p>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-zinc-950/95 backdrop-blur-xl border-t border-white/10 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex gap-3">
          <button
            onClick={handleBack}
            className="flex-1 py-3 bg-zinc-900 text-zinc-400 rounded-xl font-semibold text-sm hover:bg-zinc-800 border border-zinc-800 transition-colors"
          >
            Retour
          </button>
          <button
            disabled={!isSelected}
            onClick={handleNext}
            className={cn(
              "flex-[2] py-3 rounded-xl font-semibold text-sm transition-all shadow-[0_0_15px_rgba(16,185,129,0.3)]",
              isSelected
                ? "bg-emerald-500 text-white hover:bg-emerald-600"
                : "bg-zinc-800 text-zinc-500 cursor-not-allowed shadow-none"
            )}
          >
            {questionnaire.selectedRestaurants?.[currentDay]?.[moment] === 'picnic'
              ? 'Valider le Pique-nique'
              : 'Valider le restaurant'}
          </button>
        </div>
      </div>
    </div>
  );
};
